// apps/web/src/lib/appResume.ts
// One place that knows the app came back to the foreground, so everything that
// cares can listen for a DOM event instead of registering its own Capacitor
// listener.
//
// Coming back is not only a speed problem. An app left open overnight is still
// showing yesterday: the selected date was computed once at mount, so the first
// meal of the new day lands on the wrong one. `caloly:resumed` is what lets the
// date roll over and the day revalidate.

const EVENT = "caloly:resumed";

/** Ignore the burst of events a single foregrounding can produce, and the
 *  in-and-out of a native picker, without delaying a real return. */
const COLLAPSE_MS = 5000;

let lastFired = 0;

function fire(): void {
  const now = Date.now();
  if (now - lastFired < COLLAPSE_MS) return;
  lastFired = now;
  window.dispatchEvent(new CustomEvent(EVENT));
}

/** Wire the resume signal once from the app shell. Returns a cleanup fn. */
export function startResumeSignal(): () => void {
  // Web and PWA: the tab becoming visible again is the same event.
  const onVisible = () => { if (document.visibilityState === "visible") fire(); };
  document.addEventListener("visibilitychange", onVisible);

  let removeNative: (() => void) | null = null;
  if ((window as unknown as { Capacitor?: unknown }).Capacitor) {
    void import("@capacitor/app").then(({ App }) =>
      App.addListener("resume", fire).then((h) => { removeNative = () => h.remove(); }),
    );
  }
  return () => {
    document.removeEventListener("visibilitychange", onVisible);
    removeNative?.();
  };
}

/** Subscribe to foregrounding. Returns a cleanup fn. */
export function onResume(handler: () => void): () => void {
  window.addEventListener(EVENT, handler);
  return () => window.removeEventListener(EVENT, handler);
}
